import { useEffect, useLayoutEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft } from 'lucide-react';
import { useVersao, detalheVersao } from './RodapeVersao';

interface ModalProps {
  aberto: boolean;
  onFechar: () => void;
  titulo?: string;
  children: ReactNode;
  largura?: number | string;
  rodape?: ReactNode;
  // compacto: altura ajustada ao conteudo (sem min-height). Util pra alertas/dialogs simples.
  compacto?: boolean;
  // Sobrescreve o estilo do `.modal-body`. Existe pro modal que rola POR DENTRO (uma lista/grid
  // com scroll proprio) em vez de rolar inteiro: o CSS global poe `overflow-y: auto` no body, e
  // sem apagar isso aparecem duas barras de rolagem concorrentes. Passar `{ overflow: 'hidden' }`
  // e assumir o dever de caber — o conteudo tem que ter um filho com `overflow` proprio.
  bodyStyle?: CSSProperties;
  // telaCheia (DA-456): o modal ocupa toda a tela ABAIXO da barra de menus (DA-457), sem
  // moldura nem margem, em QUALQUER largura. Nasceu do cadastro de usuario, que ganhou a 3a
  // coluna de ponto/horario e no tablet em pe nao cabia mais na caixa. `largura` passa a ser
  // ignorada, e o drag pelo cabecalho desliga sozinho (nao ha pra onde arrastar).
  telaCheia?: boolean;
}

// Fecha so via botao X / rodape / Esc. Clique no overlay NAO fecha
// (evita perda de estado por clique acidental fora).
export function Modal({ aberto, onFechar, titulo, children, largura = 600, rodape, compacto, bodyStyle, telaCheia }: ModalProps) {
  // Modal em tela cheia COBRE o rodape da pagina, e com ele o carimbo da release — a unica
  // coisa na tela que diz qual versao esta no ar. Entao ele carrega a versao junto, e todo
  // modal `telaCheia` futuro herda isso sem ninguem precisar lembrar. Hook antes do early
  // return (regra dos hooks); o componente devolve null fora de producao.
  const versao = useVersao();

  // O modal em tela cheia comeca ABAIXO da barra de menus (DA-457) — coberta, ela levava junto
  // a orientacao de onde se esta e o caminho pra sair. A altura e MEDIDA do proprio header, nao
  // cravada: ele muda de altura por tema e por largura de tela, e um numero fixo deixaria fresta
  // (ou cortaria a barra) so no aparelho que ninguem usa pra desenvolver. `offsetHeight` +
  // ResizeObserver e a mesma receita do `useStickyHeader`.
  const [topoLivre, setTopoLivre] = useState(0);
  useLayoutEffect(() => {
    if (!aberto || !telaCheia) return;
    const header = document.querySelector<HTMLElement>('.app-header');
    if (!header) {
      setTopoLivre(0);
      return;
    }
    const medir = () => setTopoLivre(header.offsetHeight);
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(header);
    return () => ro.disconnect();
  }, [aberto, telaCheia]);

  useEffect(() => {
    if (!aberto) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFechar();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [aberto, onFechar]);

  if (!aberto) return null;

  // O modal e' desenhado num PORTAL pro <body>, nunca no lugar da arvore onde foi escrito. Um
  // overlay `position: fixed` so se apoia na tela enquanto NENHUM ancestral tiver `transform`,
  // `filter`, `contain` ou `will-change` — qualquer um deles vira o containing block e o overlay
  // passa a se medir pelo `.app-content`, que comeca ABAIXO da barra de menus: o cabecalho do
  // modal (com o X) fica atras da barra e nao ha nada pra rolar ate la. O mesmo vale pro
  // stacking context: dentro de um ancestral com z-index proprio, os z-1000 do overlay nao
  // valem mais nada. Nenhuma tela precisa lembrar disso — no <body> o modal nao tem ancestral.
  // (O tema vive no <html>, entao as variaveis de cor continuam chegando aqui.)
  return createPortal(
    <div
      className="modal-overlay"
      data-modal-tela-cheia={telaCheia ? 'true' : undefined}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', top: telaCheia ? topoLivre : undefined }}
    >
      <div
        className="modal-conteudo"
        data-componente="001"
        data-modal-compacto={compacto ? 'true' : undefined}
        data-modal-tela-cheia={telaCheia ? 'true' : undefined}
        style={{ backgroundColor: 'var(--panel-bg)', maxWidth: telaCheia ? undefined : largura, position: 'relative' }}
      >
        {titulo ? (
          <div className="modal-header">
            {/* Voltar so no telaCheia: ocupando a tela inteira ele LE como tela, e tela do sistema
                tem o voltar a esquerda do titulo. Mesma acao do X (fechar) — dois caminhos pro
                mesmo lugar, como o dblclick sempre tem o botao de clique unico ao lado. Num modal
                em caixa a moldura ja diz que aquilo flutua sobre a tela, e o X basta. */}
            {telaCheia && (
              <button type="button" className="btn-voltar-modal" onClick={onFechar} aria-label="Voltar" title="Voltar">
                <ArrowLeft size={20} />
              </button>
            )}
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titulo}</span>
            <button type="button" className="btn-fechar-modal" onClick={onFechar} aria-label="Fechar">×</button>
          </div>
        ) : (
          <button type="button" className="btn-fechar-modal btn-fechar-modal--abs" onClick={onFechar} aria-label="Fechar">×</button>
        )}
        <div className="modal-body" style={bodyStyle}>{children}</div>
        {rodape && <div className="modal-footer">{rodape}</div>}
        {/* Canto inferior direito — o MESMO lugar em que o rodape da pagina fica, pra procurar
            no automatico. Em tela cheia o rodape do modal centraliza os botoes, entao a quina
            esta livre. */}
        {telaCheia && versao && (
          <div className="modal-versao-cheia" title={detalheVersao(versao)}>
            FIODECOBRE <b>{versao.versao}</b>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
